/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
import EmberObject, { computed, set } from '@ember/object';

import * as ConversionUtils from '../utils/conversion';
import MathUtils from '../utils/math';
import * as ArrayUtils from '../utils/array';
import * as CommonUtils from '../../utils/common';
import CircuitResults from './circuit-results';
import FilterConstants from '../../filter-constants';
import CircuitDetails from './sub-results/circuit-details';
import StageDetails from './sub-results/stage-details';
import VoltageRange from './sub-results/voltage-range';
import Noise from './sub-results/noise';
import * as CircuitUtils from '../utils/circuit';
import * as ResultsUtils from '../utils/results';

/**
 * Should contain data that will be used for output or computing data for the next step in the wizard.
 */
export default class ToleranceResults extends EmberObject {
  specifications = null;
  components = null;
  toleranceInput = null;
  specificationsInput = null;
  frequencyArray = null;
  friendlyFrequencyArray = null;
  circuits = null;
  needReferenceStage = null;
  adcBandwidth = null;
  noiseFrequencyArray = null;
  power = null;
  init() {
    super.init();
    if (!CommonUtils.isNullOrUndefined(this.components)) {
      set(this, 'frequencyArray', this.components.frequencyArray);
      set(this, 'friendlyFrequencyArray', this.components.friendlyFrequencyArray);
      set(this, 'circuits', this.components.circuits);
      this.needReferenceStage = this.components.needReferenceStage;
      set(this, 'adcBandwidth', this.components.adcBandwidth);
      set(this, 'noiseFrequencyArray', this.components.noiseFrequencyArray);
      this.power = this.components.power;
    }
  }

  //TODO: this might be better if there's a function
  @computed('circuits', 'toleranceInput') get commercialCircuits() {
    var result = this.circuits.map((circuit) => {
      return CircuitUtils.toCommercialCircuit(circuit, this.toleranceInput);
    });
    return result;
  }

  //Used for testing
  @computed('components.actualStages', 'commercialCircuits', 'toleranceInput') get bigHugeCircuit() {
    let actualStages = this.components.actualStages;
    let commercialCircuits = this.commercialCircuits;
    let toleranceInput = this.toleranceInput;
    let result = [];
    for (let i = 0; i < actualStages.length; i++) {
      let implementation = actualStages[i].implementation;
      let commercialCircuit = commercialCircuits[i];
      var bigHuge = implementation.createBigHugeCircuitGroup(commercialCircuit, toleranceInput);
      result.push(bigHuge);
    }

    return result;
  }

  @computed('components', 'commercialCircuits', 'toleranceInput', 'frequencyArray') get interestingCircuitResults() {
    let actualStages = this.components.actualStages;
    let adcBandwidth = this.components.noiseInput.adcBandwidth;
    let commercialCircuits = this.commercialCircuits;
    let toleranceInput = this.toleranceInput;
    let frequencyArray = this.frequencyArray;
    let result = this._createInterestingCircuitResults(
      actualStages,
      commercialCircuits,
      toleranceInput,
      adcBandwidth,
      frequencyArray,
    );
    return result;
  }

  @computed('components', 'commercialCircuits', 'toleranceInput', 'friendlyFrequencyArray')
  get interestingCircuitResultsFriendly() {
    let actualStages = this.components.actualStages;
    let adcBandwidth = this.components.noiseInput.adcBandwidth;
    let commercialCircuits = this.commercialCircuits;
    let toleranceInput = this.toleranceInput;
    let frequencyArray = this.friendlyFrequencyArray;
    let result = this._createInterestingCircuitResults(
      actualStages,
      commercialCircuits,
      toleranceInput,
      adcBandwidth,
      frequencyArray,
    );
    return result;
  }

  @computed('commercialCircuits', 'components', 'friendlyFrequencyArray', 'toleranceInput') get circuitResults() {
    var commercialCircuits = this.commercialCircuits;
    var frequencyArray = this.friendlyFrequencyArray;
    var actualStages = this.components.actualStages;
    let adcBandwidth = this.components.noiseInput.adcBandwidth;
    let toleranceInput = this.toleranceInput;
    var circuitResults = this._createNominalCircuitResults(
      commercialCircuits,
      actualStages,
      adcBandwidth,
      frequencyArray,
      toleranceInput,
    );

    return circuitResults;
  }

  @computed('components', 'commercialCircuits', 'toleranceInput', 'noiseFrequencyArray')
  get noiseInterestingCircuitResults() {
    let actualStages = this.components.actualStages;
    let adcBandwidth = this.components.noiseInput.adcBandwidth;
    let commercialCircuits = this.commercialCircuits;
    let toleranceInput = this.toleranceInput;
    let noiseFrequencyArray = this.noiseFrequencyArray;
    let result = this._createInterestingCircuitResults(
      actualStages,
      commercialCircuits,
      toleranceInput,
      adcBandwidth,
      noiseFrequencyArray,
    );
    return result;
  }

  @computed('commercialCircuits', 'components', 'noiseFrequencyArray', 'toleranceInput') get noiseCircuitResults() {
    var commercialCircuits = this.commercialCircuits;
    var noiseFrequencyArray = this.noiseFrequencyArray;
    var actualStages = this.components.actualStages;
    let adcBandwidth = this.components.noiseInput.adcBandwidth;
    let toleranceInput = this.toleranceInput;
    var circuitResults = this._createNominalCircuitResults(
      commercialCircuits,
      actualStages,
      adcBandwidth,
      noiseFrequencyArray,
      toleranceInput,
    );

    return circuitResults;
  }

  @computed('circuitResults') get _nominalMagnitudes() {
    let circuitResults = this.circuitResults;
    let magnitudes = circuitResults.map((circuitResult) => {
      return circuitResult.magnitude;
    });

    return magnitudes;
  }

  @computed('interestingCircuitResultsFriendly') get _interestingMagnitudes() {
    let interestingCircuitMagnitudes = this.interestingCircuitResultsFriendly.map((stage) => {
      return stage.map((interestingCircuit) => {
        return interestingCircuit.magnitude;
      });
    });

    return interestingCircuitMagnitudes;
  }

  @computed('_interestingMagnitudes', '_nominalMagnitudes') get _magnitudes() {
    let interestingCircuitMagnitudes = this._interestingMagnitudes;
    let magnitudes = this._nominalMagnitudes;
    let { minimumValues, maximumValues } = ArrayUtils.getMinimumAndMaximumArray(
      interestingCircuitMagnitudes,
      magnitudes,
    );
    let minimumMagnitude = ResultsUtils.getMagnitudeArray(minimumValues);
    let maximumMagnitude = ResultsUtils.getMagnitudeArray(maximumValues);
    let nominalMagnitude = ResultsUtils.getMagnitudeArray(magnitudes);
    return { nominalMagnitude, minimumMagnitude, maximumMagnitude };
  }

  @computed('_magnitudes', 'friendlyFrequencyArray') get magnitudeDb() {
    var { nominalMagnitude, minimumMagnitude, maximumMagnitude } = this._magnitudes;
    var nominalDb = ConversionUtils.VVarrtoDBarr(nominalMagnitude);
    var minimumDb = ConversionUtils.VVarrtoDBarr(minimumMagnitude);
    var maximumDb = ConversionUtils.VVarrtoDBarr(maximumMagnitude);

    var frequencyArray = this.friendlyFrequencyArray;
    var dataPoints = ArrayUtils.joinToleranceArrays(frequencyArray, nominalDb, minimumDb, maximumDb);

    var limits = ResultsUtils.getRangeLimits(dataPoints);

    var specInputs = this.specificationsInput;
    var gainDB =
      specInputs.gainUnit === FilterConstants.gainUnits.db ? specInputs.gain : ConversionUtils.VVtoDB(specInputs.gain);
    limits.ymin = gainDB + (specInputs.stopbandAttenuation - 20);
    limits.ymax = gainDB + 10;

    return { data: dataPoints, limits: limits };
  }

  @computed('_magnitudes', 'friendlyFrequencyArray') get magnitudeVV() {
    var { nominalMagnitude, minimumMagnitude, maximumMagnitude } = this._magnitudes;
    var frequencyArray = this.friendlyFrequencyArray;
    var dataPoints = ArrayUtils.joinToleranceArrays(
      frequencyArray,
      nominalMagnitude,
      minimumMagnitude,
      maximumMagnitude,
    );

    var limits = ResultsUtils.getRangeLimits(dataPoints);
    limits.ymax += 0.2; // give 0.2 V/V headroom
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitResults', 'interestingCircuitResultsFriendly') get _phase() {
    var phases = this.circuitResults.map((item) => {
      return item.phase;
    });
    var interestingCircuitPhase = this.interestingCircuitResultsFriendly.map((stage) => {
      return stage.map((interestingCircuit) => {
        return interestingCircuit.phase;
      });
    });

    var { minimumValues, maximumValues } = ArrayUtils.getMinimumAndMaximumArray(interestingCircuitPhase, phases);
    let minimumPhase = ResultsUtils.getPhaseArray(minimumValues);
    let maximumPhase = ResultsUtils.getPhaseArray(maximumValues);
    let nominalPhase = ResultsUtils.getPhaseArray(phases);
    return { nominalPhase, minimumPhase, maximumPhase };
  }

  @computed('_phase', 'friendlyFrequencyArray') get phaseDegrees() {
    var { nominalPhase, minimumPhase, maximumPhase } = this._phase;
    var frequencyArray = this.friendlyFrequencyArray;
    var nominal = ConversionUtils.radianArrayToDegreeArray(nominalPhase);
    var minimum = ConversionUtils.radianArrayToDegreeArray(minimumPhase);
    var maximum = ConversionUtils.radianArrayToDegreeArray(maximumPhase);
    var dataPoints = ArrayUtils.joinToleranceArrays(frequencyArray, nominal, minimum, maximum);

    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var newYmax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var newYmin = limits.ymin - (limits.ymax - limits.ymin) / 10;
    limits.ymax = newYmax;
    limits.ymin = newYmin;

    var result = { data: dataPoints, limits: limits };

    return result;
  }

  @computed('_phase', 'friendlyFrequencyArray') get phaseRadians() {
    var { nominalPhase, minimumPhase, maximumPhase } = this._phase;
    var frequencyArray = this.friendlyFrequencyArray;
    var dataPoints = ArrayUtils.joinToleranceArrays(frequencyArray, nominalPhase, minimumPhase, maximumPhase);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var newYmax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var newYmin = limits.ymin - (limits.ymax - limits.ymin) / 10;
    limits.ymax = newYmax;
    limits.ymin = newYmin;

    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitResults', 'interestingCircuitResultsFriendly', 'friendlyFrequencyArray') get groupDelay() {
    var frequencyArray = this.friendlyFrequencyArray;
    var groupDelayFrequency = [];
    var nominalGroupDelayArray = this.circuitResults.map((item) => {
      let phase = item.phase;
      let { groupDelayArray, newFrequencyAxis } = ConversionUtils.groupDelay(phase, frequencyArray);
      groupDelayFrequency = newFrequencyAxis;
      return groupDelayArray;
    });

    var interestingCircuitGroupDelay = this.interestingCircuitResultsFriendly.map((stage) => {
      return stage.map((interestingCircuit) => {
        let phase = interestingCircuit.phase;
        return ConversionUtils.groupDelay(phase, frequencyArray).groupDelayArray;
      });
    });

    var { minimumValues, maximumValues } = ArrayUtils.getMinimumAndMaximumArray(
      interestingCircuitGroupDelay,
      nominalGroupDelayArray,
    );
    let minimumGroupDelay = ArrayUtils.addEnvelope(minimumValues);
    let maximumGroupDelay = ArrayUtils.addEnvelope(maximumValues);
    let nominalGroupDelay = ArrayUtils.addEnvelope(nominalGroupDelayArray);

    var dataPoints = ArrayUtils.joinToleranceArrays(
      groupDelayFrequency,
      nominalGroupDelay,
      minimumGroupDelay,
      maximumGroupDelay,
    );
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    limits.ymax = limits.ymax + (limits.ymax - limits.ymin) / 10;
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('_phase', 'friendlyFrequencyArray') get phaseDelay() {
    var { nominalPhase, minimumPhase, maximumPhase } = this._phase;
    var frequencyArray = this.friendlyFrequencyArray;
    var nominalPhaseDelay = ConversionUtils.phaseDelay(nominalPhase, frequencyArray);
    var minimumPhaseDelay = ConversionUtils.phaseDelay(minimumPhase, frequencyArray);
    var maximumPhaseDelay = ConversionUtils.phaseDelay(maximumPhase, frequencyArray);
    var dataPoints = ArrayUtils.joinToleranceArrays(
      frequencyArray,
      nominalPhaseDelay,
      minimumPhaseDelay,
      maximumPhaseDelay,
    );
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitResults') get nominalStepResponse() {
    var circuitResults = this.circuitResults;
    var stepValues = circuitResults[0].stepResponse;

    if (circuitResults.length > 1) {
      for (var i = 1; i < circuitResults.length; i++) {
        var impulseValues = circuitResults[i].impulseResponse;
        stepValues = MathUtils.convolve(stepValues, impulseValues);
      }
    }

    return stepValues;
  }

  @computed('components', 'nominalStepResponse') get stepResponse() {
    var stepResponse = this.nominalStepResponse;
    var timeAxis = this.components.timeAxis;
    var dataPoints = ArrayUtils.joinArrays(timeAxis, stepResponse);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var overreach = (limits.ymax - limits.ymin) / 10;
    limits.ymax = limits.ymax + overreach;
    if (limits.ymin < 0) {
      limits.ymin = limits.ymin - overreach;
    }
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitResults', 'interestingCircuitResultsFriendly') get _inputImpedanceMagnitude() {
    var firstCircuitResult = this.circuitResults[0];
    var nominalInputImpedanceMagnitude = firstCircuitResult.inputImpedanceMagnitude;

    var interestingCircuitInputImpedanceMagnitude = this.interestingCircuitResultsFriendly.map((stage) => {
      return stage.map((interestingCircuit) => {
        return interestingCircuit.inputImpedanceMagnitude;
      });
    });

    // Need to be array as _getMinimumAndMaximumArray takes an array of arrays
    var firstCircuitValues = [interestingCircuitInputImpedanceMagnitude[0]];
    var { minimumValues, maximumValues } = ArrayUtils.getMinimumAndMaximumArray(firstCircuitValues, [
      nominalInputImpedanceMagnitude,
    ]);
    var minimumInputImpedanceMagnitude = minimumValues[0];
    var maximumInputImpedanceMagnitude = maximumValues[0];
    return { nominalInputImpedanceMagnitude, minimumInputImpedanceMagnitude, maximumInputImpedanceMagnitude };
  }

  @computed('_inputImpedanceMagnitude', 'friendlyFrequencyArray') get inputImpedanceMagnitude() {
    var { nominalInputImpedanceMagnitude, minimumInputImpedanceMagnitude, maximumInputImpedanceMagnitude } =
      this._inputImpedanceMagnitude;
    var frequencyArray = this.friendlyFrequencyArray;
    var dataPoints = ArrayUtils.joinToleranceArrays(
      frequencyArray,
      nominalInputImpedanceMagnitude,
      minimumInputImpedanceMagnitude,
      maximumInputImpedanceMagnitude,
    );
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var newYmax = limits.ymax * 1.5;
    var newYmin = limits.ymin * 0.5;
    limits.ymax = newYmax;
    limits.ymin = newYmin;

    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed('circuitResults', 'interestingCircuitResultsFriendly') get _inputImpedancePhase() {
    var firstCircuitResult = this.circuitResults[0];
    var nominalInputImpedancePhase = firstCircuitResult.inputImpedancePhase;

    var interestingCircuitInputImpedancePhase = this.interestingCircuitResultsFriendly.map((stage) => {
      return stage.map((interestingCircuit) => {
        return interestingCircuit.inputImpedancePhase;
      });
    });

    // Need to be array as _getMinimumAndMaximumArray takes an array of arrays
    var firstCircuitValues = [interestingCircuitInputImpedancePhase[0]];
    var { minimumValues, maximumValues } = ArrayUtils.getMinimumAndMaximumArray(firstCircuitValues, [
      nominalInputImpedancePhase,
    ]);
    var minimumInputImpedancePhase = minimumValues[0];
    var maximumInputImpedancePhase = maximumValues[0];
    return { nominalInputImpedancePhase, minimumInputImpedancePhase, maximumInputImpedancePhase };
  }

  @computed('_inputImpedancePhase', 'friendlyFrequencyArray') get inputImpedancePhase() {
    var { nominalInputImpedancePhase, minimumInputImpedancePhase, maximumInputImpedancePhase } =
      this._inputImpedancePhase;
    var frequencyArray = this.friendlyFrequencyArray;
    var dataPoints = ArrayUtils.joinToleranceArrays(
      frequencyArray,
      nominalInputImpedancePhase,
      minimumInputImpedancePhase,
      maximumInputImpedancePhase,
    );
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var result = { data: dataPoints, limits: limits };
    return result;
  }

  @computed(
    'components',
    'commercialCircuits',
    'noiseFrequencyArray',
    'adcBandwidth',
    'interestingCircuitResultsFriendly',
    'circuitResults',
    'friendlyFrequencyArray',
  )
  get noise() {
    console.time('tol-noise'); //eslint-disable-line no-console
    let adcBandwidth = this.components.noiseInput.adcBandwidth;
    let noiseFrequencyArray = this.noiseFrequencyArray;
    let frequencyArray = this.friendlyFrequencyArray;
    let isSameFrequency = noiseFrequencyArray.every((item, index) => {
      return item === frequencyArray[index];
    });
    let interestingCircuitResults = isSameFrequency
      ? this.interestingCircuitResultsFriendly
      : this.noiseInterestingCircuitResults;
    let circuitResults = isSameFrequency ? this.circuitResults : this.noiseCircuitResults;
    var minArray = [];
    var maxArray = [];
    var nominalArray = [];
    let commercialMagnitude = circuitResults.map((item) => {
      return item.magnitude;
    });
    let commercialNoise = circuitResults.map((item) => {
      return item.noiseGroup.noiseReferredToOutputArray;
    });
    let allInterestingMagnitude = interestingCircuitResults.map((interestingCircuitResult) => {
      return interestingCircuitResult.map((item) => {
        return item.magnitude;
      });
    });

    let allInterestingNoise = interestingCircuitResults.map((interestingCircuitResult) => {
      return interestingCircuitResult.map((item) => {
        return item.noiseGroup.noiseReferredToOutputArray;
      });
    });

    for (let i = 0; i < circuitResults.length; i++) {
      var oneStageNoise = commercialNoise[i];
      var magnitude = commercialMagnitude[i];

      if (i === 0) {
        oneStageNoise.forEach(() => {
          minArray.push(0);
          maxArray.push(0);
          nominalArray.push(0);
        });
      }

      let comparisonNominal = this._addNoise(
        this._noiseToleranceMultiplyArrays(nominalArray, magnitude),
        oneStageNoise,
      );
      let comparisonMin = this._addNoise(this._noiseToleranceMultiplyArrays(minArray, magnitude), oneStageNoise);
      let comparisonMax = this._addNoise(this._noiseToleranceMultiplyArrays(maxArray, magnitude), oneStageNoise);

      var interestingCircuitResult = interestingCircuitResults[i];
      let interestingMagnitude = allInterestingMagnitude[i];
      let interestingNoise = allInterestingNoise[i];
      for (let j = 0; j < interestingCircuitResult.length; j++) {
        let oneStageNoiseInteresting = interestingNoise[j];
        let oneStageMagnitudeInteresting = interestingMagnitude[j];

        //create three arrays based on our interesting component object
        //let tempNominal = this._addNoise(this._noiseToleranceMultiplyArrays(nominalArray, oneStageMagnitudeInteresting), oneStageNoiseInteresting);
        let tempMin = this._addNoise(
          this._noiseToleranceMultiplyArrays(minArray, oneStageMagnitudeInteresting),
          oneStageNoiseInteresting,
        );
        let tempMax = this._addNoise(
          this._noiseToleranceMultiplyArrays(maxArray, oneStageMagnitudeInteresting),
          oneStageNoiseInteresting,
        );

        //if any point in the tempMax or tempMin are outside of our existing envelope, replace the envelope point with temp point
        for (var k = 0; k < tempMin.length; k++) {
          if (tempMin[k] < comparisonMin[k]) {
            comparisonMin[k] = tempMin[k];
          }
          if (tempMax[k] > comparisonMax[k]) {
            comparisonMax[k] = tempMax[k];
          }
        }
      }

      minArray = CommonUtils.clone(comparisonMin);
      maxArray = CommonUtils.clone(comparisonMax);
      nominalArray = CommonUtils.clone(comparisonNominal);
    }

    var result = Noise.create({
      nominalNoiseArray: nominalArray,
      minimumNoiseArray: minArray,
      maximumNoiseArray: maxArray,
      frequencyArray: frequencyArray,
      adcBandwidth: adcBandwidth,
      noiseFrequencyArray: noiseFrequencyArray,
    });
    console.timeEnd('tol-noise'); //eslint-disable-line no-console
    return result;
  }

  @computed(
    'magnitudes',
    'components.componentsInput.{vsPlus,vsMinus}',
    'circuitResults',
    'interestingCircuitResultsFriendly',
  )
  get voltageRange() {
    var vsPlus = this.components.componentsInput.vsPlus;
    var vsMinus = this.components.componentsInput.vsMinus;
    var circuitResults = this.circuitResults;
    var interestingCircuitResults = this.interestingCircuitResultsFriendly;

    // Get the worst case magnitudes per stage or use the nominal magnitude if there are no interesting circuits generated for that stage
    var worstCaseMagnitudes = interestingCircuitResults.map((stage, index) => {
      var magnitude = null;
      if (stage && stage.length > 0) {
        var magnitudeVariationsForOneStage = stage.map((interestingCircuit) => {
          return interestingCircuit.magnitude;
        });
        var minAndMaxForOneStage = ArrayUtils.getMinimumAndMaximumForOneStage(magnitudeVariationsForOneStage);
        magnitude = minAndMaxForOneStage.maxForOneStage;
      } else {
        magnitude = circuitResults[index].magnitude;
      }
      return magnitude;
    });

    var result = VoltageRange.create({
      circuitResults: circuitResults,
      vsMinus: vsMinus,
      vsPlus: vsPlus,
      worstCaseMagnitudes: worstCaseMagnitudes,
      toleranceInput: true,
    });
    return result;
  }
  @computed(
    'components.actualStages',
    'circuitResults',
    'interestingCircuitResults',
    'interestingCircuitResultsFriendly',
    'magnitudeDb.limits',
  )
  get detailsOfStages() {
    console.time('tolerance.detailsOfStages');
    var limMagnitudeDb = this.magnitudeDb.limits;
    var actualStages = this.components.actualStages;
    var interestingCircuitResults = this.interestingCircuitResults;
    var interestingCircuitResultsFriendly = this.interestingCircuitResultsFriendly;
    var circuitResults = this.circuitResults;

    var _magnitudes = this._magnitudes;
    var result = actualStages.map((actualStage, index) => {
      var targetStage = actualStage.targetStage;
      var interestingCircuitResult = interestingCircuitResults[index];
      var interestingCircuitResultFriendly = interestingCircuitResultsFriendly[index];
      var circuitResult = circuitResults[index];
      var graphData = this._getToleranceGraphData(interestingCircuitResultFriendly, circuitResult);
      graphData.limits = limMagnitudeDb;
      var res = StageDetails.create({
        targetStage: targetStage,
        actualStage: actualStage,
        circuitResult: circuitResult,
        interestingCircuitResult: interestingCircuitResult,
        graphData: graphData,
        magnitudesObject: _magnitudes,
      });
      return res;
    });

    console.timeEnd('tolerance.detailsOfStages');
    return result;
  }

  @computed('commercialCircuits', 'components') get detailsOfCircuits() {
    var circuits = this.commercialCircuits;
    var stages = this.components.actualStages;
    var componentsInput = this.components.componentsInput;
    var result = circuits.map((circuit, index) => {
      var actualStage = stages[index];
      return CircuitDetails.create({ circuit: circuit, stage: actualStage, componentsInput: componentsInput });
    });

    return result;
  }

  _getMinimumAndMaximumArray(interestingCircuitValuesArray, nominalValuesArray) {
    var minimumValues = [];
    var maximumValues = [];
    for (let i = 0; i < interestingCircuitValuesArray.length; i++) {
      if (nominalValuesArray) {
        interestingCircuitValuesArray[i].push(nominalValuesArray[i]);
      }

      var groupedByIndex = ArrayUtils.groupByIndex(interestingCircuitValuesArray[i]);
      var minForOneStage = [];
      var maxForOneStage = [];
      for (let j = 0; j < groupedByIndex.length; j++) {
        var minimum = Math.min.apply(Math, groupedByIndex[j]);
        var maximum = Math.max.apply(Math, groupedByIndex[j]);
        minForOneStage.push(minimum);
        maxForOneStage.push(maximum);
      }

      minimumValues.push(minForOneStage);
      maximumValues.push(maxForOneStage);
    }

    return { minimumValues, maximumValues };
  }

  _joinEnvelopToFrequencyArray(envelop, frequencyArray) {
    var nominalResult = ArrayUtils.joinArrays(frequencyArray, envelop.nominalValues);
    var minimumResult = ArrayUtils.joinArrays(frequencyArray, envelop.minimumValues);
    var maximumResult = ArrayUtils.joinArrays(frequencyArray, envelop.maximumValues);
    set(envelop, 'nominalValues', nominalResult);
    set(envelop, 'minimumValues', minimumResult);
    set(envelop, 'maximumValues', maximumResult);

    return envelop;
  }

  _noiseToleranceMultiplyArrays(firstArray, secondArray) {
    var combinedArray = [];
    for (let k = 0; k < firstArray.length; k++) {
      combinedArray.push(firstArray[k] * secondArray[k]);
    }

    return combinedArray;
  }

  _addNoise(noiseArrayOne, noiseArrayTwo) {
    var noiseOutputArray = [];

    for (let j = 0; j < noiseArrayOne.length; j++) {
      noiseOutputArray[j] = Math.sqrt(noiseArrayOne[j] * noiseArrayOne[j] + noiseArrayTwo[j] * noiseArrayTwo[j]);
    }
    return noiseOutputArray;
  }

  _createInterestingCircuitResults(actualStages, commercialCircuits, toleranceInput, adcBandwidth, frequencyArray) {
    let result = [];
    for (let i = 0; i < actualStages.length; i++) {
      let implementation = actualStages[i].implementation;
      let commercialCircuit = commercialCircuits[i];
      let interestingCircuits = implementation
        .getInterestingCircuits(commercialCircuit, toleranceInput)
        .filter((item) => {
          return !!item;
        });
      let interestingCircuitResults = [];
      var actualStage = actualStages[i];
      for (let j = 0; j < interestingCircuits.length; j++) {
        let interestingCircuit = interestingCircuits[j];
        let circuitResult = CircuitResults.create({
          circuit: interestingCircuit,
          actualStage: actualStage,
          adcBandwidth: adcBandwidth,
          frequencyArray: frequencyArray,
        });
        interestingCircuitResults.push(circuitResult);
      }

      result.push(interestingCircuitResults);
    }

    return result;
  }

  _createNominalCircuitResults(commercialCircuits, actualStages, adcBandwidth, frequencyArray, toleranceInput) {
    var circuitResults = commercialCircuits.map((item, index) => {
      var circuitCopy = CircuitUtils.copyCircuit(item, true, toleranceInput);
      return CircuitResults.create({
        circuit: circuitCopy,
        actualStage: actualStages[index],
        adcBandwidth: adcBandwidth,
        frequencyArray: frequencyArray,
      });
    });

    return circuitResults;
  }

  _getToleranceGraphData(interestingCircuitResult, circuitResult) {
    var magnitudes = interestingCircuitResult.map((item) => {
      return item.magnitude;
    });
    var nominalValues = circuitResult.magnitude;
    var frequencyArray = circuitResult.frequencyArray;
    magnitudes.push(nominalValues);
    var groupByIndex = ArrayUtils.groupByIndex(magnitudes);
    var minimumValues = groupByIndex.map((item) => {
      return Math.min.apply(Math, item);
    });
    var maximumValues = groupByIndex.map((item) => {
      return Math.max.apply(Math, item);
    });
    var nominal = ConversionUtils.VVarrtoDBarr(nominalValues);
    var minimum = ConversionUtils.VVarrtoDBarr(minimumValues);
    var maximum = ConversionUtils.VVarrtoDBarr(maximumValues);
    var dataPoints = ArrayUtils.joinToleranceArrays(frequencyArray, nominal, minimum, maximum);
    var limits = ResultsUtils.getRangeLimits(dataPoints);
    var result = { data: dataPoints, limits: limits };
    return result;
  }
}
